import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Button, 
  Input, 
  Space, 
  List, 
  Tag, 
  Alert,
  Spin
} from 'antd';
import { 
  SearchOutlined, 
  DeleteOutlined,
  MonitorOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { logEvent } from '../../analytics';
import createCheck from '../../utils/drugInteraction/createCheck';
import PatientSelect from '../patient/PatientSelect';
import fetchPatient from '../../utils/patient/fetchPatient';
import useDrugInteractionsPolling from '../../hooks/useDrugInteractionsPolling';
import ThumbsRating from '../ThumbsRating';

const { Text } = Typography;

const DrugInteractionsAnalyzeTab = ({ medications: initialMedications = [], patientUuid: initialPatientUuid = null }) => {
  const { t } = useTranslation();
  
  const [medications, setMedications] = useState(initialMedications);
  const [newMedication, setNewMedication] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isLoadingPatient, setIsLoadingPatient] = useState(false);
  const [isMedicationInputFocused, setIsMedicationInputFocused] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCheckingInteractions, setIsCheckingInteractions] = useState(false);
  const [checkUuid, setCheckUuid] = useState(null);

  const { drugInteractions, isPolling, error, startPolling, clearResults } = useDrugInteractionsPolling();

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load patient from props - only if props are provided
  useEffect(() => {
    const loadPatient = async () => {
      if (initialPatientUuid) {
        setIsLoadingPatient(true);
        try {
          const patient = await fetchPatient(initialPatientUuid);
          setSelectedPatient(patient);
          logEvent('DrugInteractions', 'LoadPatient', { 
            patientUuid: patient.getUUID(),
            patientName: patient.getFullName(),
            source: 'props'
          });
        } catch (error) {
          console.error('Failed to load patient:', error);
          setSelectedPatient(null);
        } finally {
          setIsLoadingPatient(false);
        }
      }
    };

    loadPatient();
  }, [initialPatientUuid]); // Run when initialPatientUuid changes

  // Initialize medications from props - only if props are provided
  useEffect(() => {
    if (initialMedications && initialMedications.length > 0) {
      setMedications(initialMedications);
    }
  }, [initialMedications]); // Run when initialMedications changes

  const handleCheckInteractions = async () => {
    if (medications.length === 0) {
      return;
    }

    setIsCheckingInteractions(true);
    const patientUuid = selectedPatient?.getUUID() || null;
    
    logEvent('DrugInteractions', 'CheckInteractions');
    
    try {
      console.log('[DrugInteractionsAnalyzeTab] Starting createCheck...');
      const initialPresenter = await createCheck(medications, patientUuid);
      const uuid = initialPresenter.getUUID();
      setCheckUuid(uuid);
      
      await startPolling(uuid);
      console.log('[DrugInteractionsAnalyzeTab] Polling started successfully');
    } catch (error) {
      console.error('Failed to check drug interactions', error);
    } finally {
      setIsCheckingInteractions(false);
    }
  };

  const handleAddMedication = () => {
    if (newMedication.trim()) {
      // Split by comma and clean up each medication
      const medicationsToAdd = newMedication
        .split(',')
        .map(med => med.trim())
        .filter(med => med.length > 0);
      
      if (medicationsToAdd.length > 0) {
        setMedications(prev => [...prev, ...medicationsToAdd]);
        // Clear previous results when adding new medications
        if (drugInteractions) {
          clearResults();
        }
        logEvent('DrugInteractions', 'AddMedication', { 
          medicationCount: medicationsToAdd.length,
          medications: medicationsToAdd 
        });
        setNewMedication('');
      }
    }
  };

  const handleRemoveMedication = (index) => {
    setMedications(prev => prev.filter((_, i) => i !== index));
    // Clear previous results when medications are removed
    if (drugInteractions) {
      clearResults();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleAddMedication();
    }
  };

  const handlePatientSelect = async (patient) => {
    // If a patient is selected, fetch the full patient data with sessions
    if (patient) {
      try {
        const fullPatient = await fetchPatient(patient.getUUID());
        setSelectedPatient(fullPatient);
        
        // If patient has medications, add them to existing medications
        if (fullPatient.hasMedications()) {
          const lastDoctorNote = fullPatient.getLastDoctorNote();
          const medicationStrings = lastDoctorNote.getActiveMedicationStrings();
          
          // Add patient medications to existing medications, avoiding duplicates
          setMedications(prevMedications => {
            const existingMedications = prevMedications || [];
            const newMedications = medicationStrings.filter(med => 
              !existingMedications.some(existing => 
                existing.toLowerCase().trim() === med.toLowerCase().trim()
              )
            );
            return [...existingMedications, ...newMedications];
          });
          
          // Clear previous results when patient changes
          if (drugInteractions) {
            clearResults();
          }
        }
        // Don't clear medications if patient has no medications - keep existing ones
        logEvent('DrugInteractions', 'SelectPatient');
      } catch (error) {
        console.error('Failed to fetch patient with sessions:', error);
        // Fall back to the basic patient data without sessions
        setSelectedPatient(patient);
        // Don't clear medications on error - keep existing ones
      }
    } else {
      // No patient selected
      setSelectedPatient(null);
      // Don't clear medications when deselecting patient - keep existing ones
      logEvent('DrugInteractions', 'SelectPatient', { 
        patientUuid: null,
        patientName: null
      });
    }
  };

  const handleAddNewPatient = () => {
    // For now, we'll just clear the selection
    // In the future, this could open a patient creation modal
    setSelectedPatient(null);
    setMedications([]);
    logEvent('DrugInteractions', 'AddNewPatient');
  };

  const handleClear = () => {
    setSelectedPatient(null);
    setMedications([]);
    setNewMedication('');
    setCheckUuid(null);
    setIsCheckingInteractions(false);
    clearResults();
    logEvent('DrugInteractions', 'ClearAll');
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'major': return 'red';
      case 'moderate': return 'orange';
      case 'minor': return 'default';
      default: return 'default';
    }
  };

  const getSeverityText = (severity) => {
    switch (severity) {
      case 'major': return t('drug_interactions.severity.major');
      case 'moderate': return t('drug_interactions.severity.moderate');
      case 'minor': return t('drug_interactions.severity.minor');
      case 'none': return t('drug_interactions.severity.none');
      default: return severity;
    }
  };

  const getSeverityOrder = (severity) => {
    switch (severity) {
      case 'major': return 0;
      case 'moderate': return 1;
      case 'minor': return 2;
      case 'none': return 3;
      default: return 4;
    }
  };

  const sortInteractionsBySeverity = (interactions) => {
    return [...interactions].sort((a, b) => {
      const orderA = getSeverityOrder(a.severity);
      const orderB = getSeverityOrder(b.severity);
      return orderA - orderB;
    });
  };

  const renderResults = () => {
    if (!drugInteractions) {
      return null;
    }

    if (drugInteractions.hasNoInteractions()) {
      return (
        <div data-testid="no-interactions-results" style={{ marginTop: 16, paddingBottom: 32 }}>
          <Alert
            data-testid="no-interactions-alert"
            message={t('drug_interactions.no_interactions_found')}
            description={t('drug_interactions.no_interactions_description')}
            type="success"
            showIcon
            style={{ marginBottom: 16 }}
          />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ThumbsRating
              rateableUuid={drugInteractions.getUUID()}
              rateableType="DrugInteractionCheck"
              hint={t('drug_interactions.rate_results_hint')}
            />
          </div>
        </div>
      );
    }

    return (
      <div data-testid="interactions-results" style={{ marginTop: 50, paddingBottom: 32 }}>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong style={{ fontSize: '16px' }}>{t('drug_interactions.results')}</Text>
          <ThumbsRating
            rateableUuid={drugInteractions.getUUID()}
            rateableType="DrugInteractionCheck"
            hint={t('drug_interactions.rate_results_hint')}
          />
        </div>

        <List
          data-testid="interactions-list"
          dataSource={sortInteractionsBySeverity(drugInteractions.getInteractions())}
          renderItem={(interaction, index) => (
            <div 
              key={index}
              data-testid={`interaction-card-${index}`}
              style={{ 
                border: '1px solid #f0f0f0', 
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#fafafa'
              }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                  <Tag color={getSeverityColor(interaction.severity)} style={{ fontSize: '12px' }}>
                    {getSeverityText(interaction.severity)}
                  </Tag>
                </div>
                
                <Text style={{ fontSize: '15px', color: '#666', lineHeight: '1.5' }}>
                  {interaction.description}
                </Text>
                
                {/* Patient-Specific Recommendation FIRST, green */}
                {interaction.recommendationPatientSpecific && selectedPatient && (interaction.severity === 'major' || interaction.severity === 'moderate') && (
                  <div style={{ 
                    backgroundColor: '#f6ffed', // green background
                    padding: '8px 12px', 
                    borderRadius: '4px',
                    border: '1px solid #b7eb8f', // green border
                    marginBottom: interaction.recommendationGeneral ? '8px' : '0'
                  }}>
                    <Text style={{ fontSize: '14px', color: '#333', fontStyle: 'italic', lineHeight: '1.4' }}>
                      <strong>{t('drug_interactions.patient_specific_recommendation')}:</strong> {interaction.recommendationPatientSpecific}
                    </Text>
                  </div>
                )}

                {/* General Recommendation SECOND, blue */}
                {interaction.recommendationGeneral && (interaction.severity === 'major' || interaction.severity === 'moderate') && (
                  <div style={{ 
                    backgroundColor: '#e6f7ff', // blue background
                    padding: '8px 12px', 
                    borderRadius: '4px',
                    border: '1px solid #91d5ff', // blue border
                  }}>
                    <Text style={{ fontSize: '14px', color: '#333', fontStyle: 'italic', lineHeight: '1.4' }}>
                      <strong>{t('drug_interactions.recommendation')}:</strong> {interaction.recommendationGeneral}
                    </Text>
                  </div>
                )}
              </Space>
            </div>
          )}
        />
      </div>
    );
  };

  return (
    <div data-testid="drug-interactions-quick-check">
      {/* Patient Selection and Medication Input Row */}
      <div 
        data-testid="patient-medication-row"
        style={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          gap: 16, 
          marginBottom: 16
        }}
      >
        {/* Medications Input - First on desktop, Second on mobile */}
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
            <Text style={{ fontSize: '12px', color: '#666' }}>
              {t('drug_interactions.medications')}
            </Text>
          </div>
          <div className="medications-input-wrapper">
            <Input
              data-testid="medication-input"
              placeholder={!isMedicationInputFocused ? t('drug_interactions.placeholder') : ''}
              value={newMedication}
              onChange={(e) => setNewMedication(e.target.value)}
              onKeyDown={handleKeyPress}
              suffix={<SearchOutlined 
                style={{ color: '#999', fontSize: 12, cursor: 'pointer' }} 
                onClick={handleAddMedication}
              />}
              onFocus={(e) => {
                setIsMedicationInputFocused(true);
              }}
              onBlur={(e) => {
                setIsMedicationInputFocused(false);
              }}
            />
          </div>
        </div>

        {/* Patient Selection - Second on desktop, First on mobile */}
        <div style={{ flex: 1, position: 'relative' }}>
          {isLoadingPatient ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <Text style={{ fontSize: '12px', color: '#666' }}>
                  {t('drug_interactions.patient_selection')}
                </Text>
              </div>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                height: '44px',
                backgroundColor: '#fafafa',
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                padding: '0 12px'
              }}>
                <Spin size="small" />
                <Text type="secondary" style={{ marginLeft: 8, fontSize: '14px' }}>
                  {t('drug_interactions.loading_patient')}
                </Text>
              </div>
            </div>
          ) : selectedPatient ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <Text style={{ fontSize: '12px', color: '#666' }}>
                  {t('drug_interactions.patient_selection')}
                </Text>
              </div>
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '8px 12px',
                backgroundColor: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                height: '44px'
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Text style={{ fontSize: '14px', color: '#333', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedPatient.getFullName()}
                  </Text>
                  <Text type="secondary" style={{ fontSize: '12px', display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {selectedPatient.getAge() && `${selectedPatient.getAgeDisplay()}`}
                    {selectedPatient.getGenderSubject() && ` • ${selectedPatient.getGenderSubject()}`}
                  </Text>
                </div>
                <Button
                  data-testid="remove-patient-button"
                  size="small"
                  type="text"
                  icon={<DeleteOutlined />}
                  onClick={() => handlePatientSelect(null)}
                  style={{ color: '#999', flexShrink: 0 }}
                />
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 4 }}>
                <Text style={{ fontSize: '12px', color: '#666' }}>
                  {t('drug_interactions.patient_selection')}
                </Text>
              </div>
              <PatientSelect
                selectedPatient={selectedPatient}
                onSelect={handlePatientSelect}
                onAddNewPatient={handleAddNewPatient}
                placeholder={t('drug_interactions.select_patient_placeholder')}
                selectStyle={{ fontSize: '14px', height: '44px' }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Patient hint text - only show when no patient is selected */}
      {!selectedPatient && (
        <div style={{ 
          textAlign: 'right', 
          marginBottom: 26,
          marginTop: -10
        }}>
          <Text type="secondary" style={{ 
            fontSize: '13px', 
            color: '#666', 
            fontStyle: 'italic',
            whiteSpace: isMobile ? 'normal' : 'nowrap'
          }}>
            {t('drug_interactions.patient_hint')}
          </Text>
        </div>
      )}
      
      {/* Medications list */}
      {medications && medications.length > 0 ? (
        <div style={{ marginBottom: 16 }}>
          <List
            data-testid="medications-list"
            size="small"
            dataSource={medications}
            renderItem={(medication, index) => (
              <List.Item
                key={`medication-${index}-${medication}`}
                style={{ 
                  padding: '6px 12px 6px 12px', 
                  fontStyle: 'italic', 
                  fontSize: '14px',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}
                actions={[
                  <Button
                    data-testid={`remove-medication-${index}`}
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    style={{ color: '#999', marginRight: '-8px' }}
                    onClick={() => handleRemoveMedication(index)}
                  />
                ]}
              >
                <Text data-testid={`medication-text-${index}`} style={{ fontWeight: 500, color: '#495057' }}>
                  {medication}
                </Text>
              </List.Item>
            )}
          />
        </div>
      ) : null}

      {/* Patient Context Note */}
      {selectedPatient && (
        <div style={{ marginBottom: 16 }}>
          <Alert
            message={t('drug_interactions.personalized_interactions_note')}
            type="success"
            showIcon={false}
            style={{ fontSize: '14px' }}
          />
        </div>
      )}

      {/* Main call-to-action button */}
      <div style={{ textAlign: 'center', marginTop: 20 }}>
        <Space>
          <Button
            data-testid="check-interactions-button"
            icon={<MonitorOutlined />}
            type="primary"
            onClick={handleCheckInteractions}
            loading={isCheckingInteractions || isPolling}
            disabled={!medications || medications.length < 2 || (drugInteractions && !isCheckingInteractions && !isPolling)}
            size="large"
          >
            {isPolling ? t('drug_interactions.checking_interactions') : t('drug_interactions.check_interactions')}
          </Button>
          
          {/* Clear button - show when there are medications, patient, or results */}
          {(medications && medications.length > 0) || selectedPatient || drugInteractions ? (
            <Button
              data-testid="clear-results-button"
              type="default"
              onClick={handleClear}
              size="large"
            >
              {t('drug_interactions.clear_results')}
            </Button>
          ) : null}
        </Space>
      </div>

      {/* Show results in the same tab */}
      {renderResults()}
    </div>
  );
};

export default DrugInteractionsAnalyzeTab; 